package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentProblemResponse
import ai.jessie.shared.documents.DocumentResponse
import ai.jessie.shared.documents.DocumentValidationResponse
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.decodeProblem
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.serialization.json.Json
import org.w3c.files.File
import org.w3c.xhr.FormData
import org.w3c.xhr.ProgressEvent
import org.w3c.xhr.XMLHttpRequest

// UC-017. Validate (steps 3-5) and upload (steps 6-9) send the same single-file multipart body; the
// server repeats every check at upload (B3). `progress` receives the sent share in percent.
interface DocumentPort {
    suspend fun inbox(organisationId: String): List<DocumentResponse>

    // UC-020 B8: the documents in one workflow state; the Inbox keeps [inbox].
    suspend fun list(organisationId: String, status: DocumentFilter): List<DocumentResponse>

    suspend fun validate(
        organisationId: String,
        file: File,
        progress: (Int) -> Unit = {},
    ): DocumentValidationResponse

    suspend fun upload(
        organisationId: String,
        file: File,
        progress: (Int) -> Unit = {},
    ): DocumentResponse
}

val LocalDocumentPort =
    staticCompositionLocalOf<DocumentPort> {
        error("Document port must be supplied by the application")
    }

// Status 0 is a transfer that did not reach a server answer.
class DocumentRequestFailure(val status: Int, val detail: DocumentProblemResponse) :
    Exception("Document request failed")

class BrowserDocumentPort : DocumentPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun inbox(organisationId: String) =
        json.decodeFromString<List<DocumentResponse>>(request(organisationId, ""))

    override suspend fun list(organisationId: String, status: DocumentFilter) =
        json.decodeFromString<List<DocumentResponse>>(
            request(organisationId, "?status=${status.name}")
        )

    override suspend fun validate(organisationId: String, file: File, progress: (Int) -> Unit) =
        json.decodeFromString<DocumentValidationResponse>(
            send(organisationId, "/validate", file, HTTP_OK, progress)
        )

    override suspend fun upload(organisationId: String, file: File, progress: (Int) -> Unit) =
        json.decodeFromString<DocumentResponse>(
            send(organisationId, "", file, HTTP_CREATED, progress)
        )

    private suspend fun request(organisationId: String, path: String): String {
        require(validOrganisationId(organisationId))
        return apiRequest(json, "/organisations/$organisationId/documents$path", null) {
            status,
            text ->
            json.documentFailure(status, text)
        }
    }

    // XMLHttpRequest, because fetch reports no upload progress. Same-origin, so the session cookie
    // and the browser's Origin header go with it; cancelling the caller aborts the transfer.
    private suspend fun send(
        organisationId: String,
        path: String,
        file: File,
        success: Int,
        progress: (Int) -> Unit,
    ): String {
        require(validOrganisationId(organisationId))
        val body = FormData().apply { append("file", file, file.name) }
        return suspendCancellableCoroutine { continuation ->
            val xhr = XMLHttpRequest()
            xhr.open("POST", "/api/v1/organisations/$organisationId/documents$path")
            xhr.upload.onprogress = { event ->
                val sent = event as ProgressEvent
                if (sent.lengthComputable && sent.total.toDouble() > 0)
                    progress((sent.loaded.toDouble() * PERCENT / sent.total.toDouble()).toInt())
            }
            xhr.onload = {
                val status = xhr.status.toInt()
                if (status == success) continuation.resume(xhr.responseText)
                else
                    continuation.resumeWithException(json.documentFailure(status, xhr.responseText))
            }
            val interrupted = { _: dynamic ->
                if (continuation.isActive)
                    continuation.resumeWithException(
                        DocumentRequestFailure(0, DocumentProblemResponse(NETWORK_UNAVAILABLE))
                    )
            }
            xhr.onerror = interrupted
            xhr.onabort = interrupted
            xhr.ontimeout = interrupted
            continuation.invokeOnCancellation { xhr.abort() }
            xhr.send(body)
        }
    }
}

// Document problem and tenant error bodies both carry `code`; a body that is not JSON (for example
// the proxy's own 413 answer) keeps only the status.
internal fun Json.documentFailure(status: Int, text: String): DocumentRequestFailure {
    val detail = decodeProblem(text, DocumentProblemResponse(""))
    return DocumentRequestFailure(status, detail)
}

// Document public IDs share the organisation ID format.
fun validDocumentId(value: String) = validOrganisationId(value)

internal const val NETWORK_UNAVAILABLE = "NETWORK_UNAVAILABLE"
private const val HTTP_OK = 200
private const val HTTP_CREATED = 201
private const val PERCENT = 100
